/**
 * Validação/formatação de documento brasileiro (CPF, CNPJ). Portado de
 * `admin-dashboard/src/shared/utils/br-fields.ts` — mesma regra, para os
 * dois apps concordarem sobre o que é um valor válido. Só visual: nunca usar
 * para bloquear cadastro (o campo `doc` sempre salva como veio, inclusive de
 * webhook/sistema externo).
 */

export const onlyDigits = (value: string): string => value.replace(/\D/g, '');

/**
 * Máscara `000.000.000-00` que aceita entrada parcial.
 */
export function formatCPF(value: string): string {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** Valida os dois dígitos verificadores do CPF. */
export function isValidCPF(value: string): boolean {
  const cpf = onlyDigits(value);
  // Sequências repetidas (000.000.000-00, 111...) passam no cálculo, mas não
  // são CPFs reais.
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(cpf[i], 10) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf[9], 10)) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(cpf[i], 10) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  return resto === parseInt(cpf[10], 10);
}

/**
 * Máscara `00.000.000/0000-00` que aceita entrada parcial.
 */
export function formatCNPJ(value: string): string {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Valida os dois dígitos verificadores do CNPJ. */
export function isValidCNPJ(value: string): boolean {
  const cnpj = onlyDigits(value);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;

  const calcDigit = (base: string): number => {
    const weights =
      base.length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = base
      .split('')
      .reduce((acc, digit, i) => acc + parseInt(digit, 10) * weights[i], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const primeiroDigito = calcDigit(cnpj.slice(0, 12));
  if (primeiroDigito !== parseInt(cnpj[12], 10)) return false;

  const segundoDigito = calcDigit(cnpj.slice(0, 13));
  return segundoDigito === parseInt(cnpj[13], 10);
}

/**
 * Documento brasileiro genérico: o campo `doc` aceita tanto pessoa física
 * quanto jurídica (ex.: aluno cadastrado com CNPJ da empresa que paga a
 * matrícula) — o tipo é inferido pela quantidade de dígitos, nunca imposto.
 */
export type DocumentType = 'cpf' | 'cnpj';

/** `null` quando não dá pra dizer (nem 11 nem 14 dígitos) — não é erro, é indefinido. */
export function detectDocumentType(
  value: string | null | undefined,
): DocumentType | null {
  if (!value) return null;
  const len = onlyDigits(value).length;
  if (len === 11) return 'cpf';
  if (len === 14) return 'cnpj';
  return null;
}

/**
 * Só usado para exibição/feedback visual (bolinha verde) — nunca para
 * bloquear cadastro ou persistência.
 */
export function isValidDocument(value: string | null | undefined): boolean {
  const type = detectDocumentType(value);
  if (type === 'cpf') return isValidCPF(value ?? '');
  if (type === 'cnpj') return isValidCNPJ(value ?? '');
  return false;
}

/**
 * Formata para leitura. Só mascara o que realmente tem cara de CPF (11
 * dígitos) ou CNPJ (14) — dados legados podem trazer outro documento, e nesse
 * caso é melhor mostrar como está do que forçar um formato errado.
 */
export function formatDocumentForDisplay(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const type = detectDocumentType(value);
  if (type === 'cpf') return formatCPF(value);
  if (type === 'cnpj') return formatCNPJ(value);
  return value;
}
